<script>
    import moment from "moment";

    import { Route, navigateTo } from "svelte-router-spa";

    import Navbar from "../../../components/navbar/Navbar.svelte";
    import CustomerNotes from "../../../components/customer/CustomerNotes.svelte";
    import RecentOperations from "../../../components/customer/RecentOperations.svelte";
    import AdditionalPhoneNumbers from "../../../components/customer/AdditionalPhoneNumbers.svelte";
    import AdditionalAddresses from "../../../components/customer/AdditionalAddresses.svelte";
    import DevicesBeingUsed from "../../../components/customer/DevicesBeingUsed.svelte";
    import FilterTypes from "../../../components/customer/FilterTypes.svelte";

    export let currentRoute;
    export let params;

    const customerId = currentRoute.childRoute.namedParams["id"];
    let customer = {};
    let recentOperations = [];
    let customerNotes = [];
    let additionalPhoneNumbers = [];
    let additionalAddresses = [];
    let devicesBeingUsed = [];
    let filterTypes = [];

    const maxListItems = 500;

    let isEditingDisabled = true;
    let customerError = false;
    let error = null;

    let promise = fetch(process.env.BACKEND_URL + "customers/" + customerId, {
        method: "GET",
        credentials: "include",
    })
        .then((data) => data.json())
        .then((data) => {
            customer = data;
            customerNotes = customer.customerNotes.sort(
                (a, b) => new Date(b.date) - new Date(a.date),
            );
            recentOperations = customer.recentOperations.sort(
                (a, b) => new Date(b.date) - new Date(a.date),
            );
            additionalPhoneNumbers = customer.additionalPhoneNumbers;
            additionalAddresses = customer.additionalAddresses;
            devicesBeingUsed = customer.devicesBeingUsed;
            filterTypes = customer.filterTypes;
            return data;
        })
        .catch((error) => {
            customerError = true;
        });

    function toggleEditing() {
        isEditingDisabled = !isEditingDisabled;
    }

    function addOperation(operation) {
        recentOperations.push({
            _id: recentOperations.length + 1,
            date: moment().format("YYYY-MM-DD"),
            note: operation.trim(),
        });
        recentOperations = recentOperations;
    }

    function deleteOperation(id) {
        recentOperations = recentOperations.filter(
            (operation) => operation._id !== id,
        );
    }

    function addNote(note) {
        customerNotes.push({
            _id: customerNotes.length + 1,
            date: moment().format("YYYY-MM-DD"),
            note: note.trim(),
        });
        customerNotes = customerNotes;
    }

    function deleteNote(id) {
        customerNotes = customerNotes.filter((note) => note._id !== id);
    }

    function addNumber(number) {
        const [owner, phoneNumber] = number.split("-");
        additionalPhoneNumbers.push({
            _id: additionalPhoneNumbers.length + 1,
            owner: owner,
            phoneNumber: phoneNumber,
        });
        additionalPhoneNumbers = additionalPhoneNumbers;
    }

    function deleteNumber(id) {
        additionalPhoneNumbers = additionalPhoneNumbers.filter(
            (number) => number._id !== id,
        );
    }

    function addAddress(newAddress) {
        const [owner, address] = newAddress.split("-");
        additionalAddresses.push({
            _id: additionalAddresses.length + 1,
            owner: owner,
            address: address,
        });
        additionalAddresses = additionalAddresses;
    }

    function deleteAddress(id) {
        additionalAddresses = additionalAddresses.filter(
            (address) => address._id !== id,
        );
    }

    function addDevice(newDevice) {
        devicesBeingUsed.push({
            _id: devicesBeingUsed.length + 1,
            device: newDevice,
        });
        devicesBeingUsed = devicesBeingUsed;
    }

    function deleteDevice(id) {
        devicesBeingUsed = devicesBeingUsed.filter(
            (device) => device._id !== id,
        );
    }

    function addFilter(newFilter) {
        filterTypes.push({
            _id: filterTypes.length + 1,
            filter: newFilter,
        });
        filterTypes = filterTypes;
    }

    function deleteFilter(id) {
        filterTypes = filterTypes.filter((filter) => filter._id !== id);
    }

    function updateCallDate() {
        customer.callDate = moment(customer.filterChangeDate)
            .subtract(1, "days")
            .format("YYYY-MM-DD");
    }

    function cleanBeforeRequest() {
        recentOperations = recentOperations.map((operation) => {
            if (operation._id < maxListItems) {
                delete operation._id;
            }
            return operation;
        });

        customerNotes = customerNotes.map((note) => {
            if (note._id < maxListItems) {
                delete note._id;
            }
            return note;
        });

        additionalPhoneNumbers = additionalPhoneNumbers.map((number) => {
            if (number._id < maxListItems) {
                delete number._id;
            }
            return number;
        });

        additionalAddresses = additionalAddresses.map((address) => {
            if (address._id < maxListItems) {
                delete address._id;
            }
            return address;
        });

        devicesBeingUsed = devicesBeingUsed.map((device) => {
            if (device._id < maxListItems) {
                delete device._id;
            }
            return device;
        });

        filterTypes = filterTypes.map((filter) => {
            if (filter._id < maxListItems) {
                delete filter._id;
            }
            return filter;
        });

        customer.customerNotes = customerNotes;
        customer.recentOperations = recentOperations;
        customer.additionalPhoneNumbers = additionalPhoneNumbers;
        customer.additionalAddresses = additionalAddresses;
        customer.devicesBeingUsed = devicesBeingUsed;
        customer.filterTypes = filterTypes;
        delete customer.message;
    }

    async function addUser() {
        cleanBeforeRequest();
        const response = await fetch(process.env.BACKEND_URL + "customers", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(customer),
            credentials: "include",
        });

        if (response.ok) {
            navigateTo("/");
        } else {
            error =
                "Müşteri bilgilerinde eksik/hatalı giriş bulunmaktadır. Kontrol edip tekrar deneyiniz.";
        }
    }

    async function updateUser() {
        cleanBeforeRequest();
        const response = await fetch(
            process.env.BACKEND_URL + "customers/" + customer._id,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(customer),
                credentials: "include",
            },
        );

        if (response.ok) {
            navigateTo("/");
        } else {
            error =
                "Müşteri bilgilerinde eksik/hatalı giriş bulunmaktadır. Kontrol edip tekrar deneyiniz.";
        }
    }

    async function deleteUser() {
        const response = await fetch(
            process.env.BACKEND_URL + "customers/" + customer._id,
            {
                method: "DELETE",
                credentials: "include",
            },
        );

        if (response.ok) {
            navigateTo("/");
        } else {
            error =
                "Müşteriyi silme işleminde hata meydana geldi. Lütfen daha sonra tekrar deneyiniz.";
        }
    }
</script>

<svelte:head>
    <title>Müşteri Bilgileri</title>
</svelte:head>

<div
    class="modal fade"
    id="staticBackdrop"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
>
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="staticBackdropLabel">
                    Emin Misiniz?
                </h5>
                <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    data-bs-theme="light"
                ></button>
            </div>
            <div class="modal-body">
                Seçili müşteriyi veritabanından silmek üzeresiniz. İlerlemeye
                devam etmek istiyor musunuz?
            </div>
            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal">İptal Et</button
                >
                <button
                    type="button"
                    class="btn btn-danger"
                    data-bs-dismiss="modal"
                    on:click={deleteUser}
                    >Evet, müşteriyi silmek istiyorum.</button
                >
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="phoneNumbersModal"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
>
    <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title">Ek Telefon Numaraları</h5>
                <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    data-bs-theme="light"
                ></button>
            </div>
            <div class="modal-body">
                <AdditionalPhoneNumbers
                    {additionalPhoneNumbers}
                    {deleteNumber}
                    {addNumber}
                />
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="additionalAddressesModal"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
>
    <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title">Tüm Adres Bilgileri</h5>
                <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    data-bs-theme="light"
                ></button>
            </div>
            <div class="modal-body">
                <AdditionalAddresses
                    {additionalAddresses}
                    {deleteAddress}
                    {addAddress}
                />
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="customerNotesModal"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
>
    <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title">Müşteri Notları</h5>
                <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    data-bs-theme="light"
                ></button>
            </div>
            <div class="modal-body">
                <CustomerNotes {customerNotes} {deleteNote} {addNote} />
            </div>
        </div>
    </div>
</div>

{#await promise then data}
    <Navbar />
    <div
        class="d-flex flex-wrap align-items-center justify-content-center text-center"
    >
        <div class="container mt-5">
            <div class="d-flex flex-row justify-content-evenly">
                <button
                    class="btn btn-primary mb-5"
                    on:click|preventDefault={toggleEditing}
                    >{isEditingDisabled
                        ? "Düzenlemeyi Etkinleştir"
                        : "Düzenlemeyi Sonlandır"}</button
                >
                {#if !customerError}
                    <button
                        type="button"
                        class="btn btn-danger mb-5"
                        data-bs-toggle="modal"
                        data-bs-target="#staticBackdrop">Müşteriyi Sil</button
                    >
                {/if}
            </div>
            <form
                on:submit|preventDefault={customerError ? addUser : updateUser}
            >
                <fieldset disabled={isEditingDisabled}>
                    <div class="row mt-2 mb-3">
                        <div class="col-md-6">
                            <label for="name" class="form-label">İsim</label>
                            <input
                                type="text"
                                class="form-control text-center"
                                id="name"
                                required
                                bind:value={customer.name}
                            />
                        </div>
                        <div class="col-md-4">
                            <label for="phoneNumber" class="form-label"
                                >Telefon Numarası</label
                            >
                            <input
                                type="text"
                                class="form-control text-center"
                                id="phoneNumber"
                                required
                                bind:value={customer.phoneNumber}
                            />
                        </div>
                        <div class="col-md-2">
                            <label
                                for="additionalPhoneNumbers"
                                class="form-label">Ek Telefon Numaraları</label
                            >
                            <button
                                type="button"
                                class="btn btn-info"
                                data-bs-toggle="modal"
                                data-bs-target="#phoneNumbersModal"
                                >Görüntüle</button
                            >
                        </div>
                    </div>
                    <div class="row mt-2 mb-3">
                        <div class="col-md-4">
                            <label for="district" class="form-label">İlçe</label
                            >
                            <input
                                type="text"
                                class="form-control text-center"
                                id="district"
                                required
                                bind:value={customer.district}
                            />
                        </div>
                        <div class="col-md-6">
                            <label for="address" class="form-label">Adres</label
                            >
                            <textarea
                                class="form-control text-center"
                                style="resize: none; height: 90px;"
                                id="address"
                                required
                                bind:value={customer.address}
                            />
                        </div>
                        <div class="col-md-2">
                            <label for="additionalAddresses" class="form-label"
                                >Tüm Adres Bilgileri</label
                            >
                            <button
                                type="button"
                                class="btn btn-info"
                                data-bs-toggle="modal"
                                data-bs-target="#additionalAddressesModal"
                                >Görüntüle</button
                            >
                        </div>
                    </div>
                    <div class="row mt-2 mb-3">
                        <DevicesBeingUsed
                            {devicesBeingUsed}
                            {deleteDevice}
                            {addDevice}
                        />
                        <FilterTypes {filterTypes} {deleteFilter} {addFilter} />
                    </div>
                    <div class="row mt-2 mb-3">
                        <div class="mb-3 col-md-6">
                            <label for="assemblyDate" class="form-label"
                                >Montaj Tarihi</label
                            >
                            <input
                                type="date"
                                class="form-control text-center"
                                id="assemblyDate"
                                max={moment(new Date()).format("YYYY-MM-DD")}
                                bind:value={customer.assemblyDate}
                            />
                        </div>
                        <div class="mb-3 col-md-6">
                            <label for="callDate" class="form-label"
                                >Arama Tarihi</label
                            >
                            <input
                                type="date"
                                class="form-control text-center"
                                id="callDate"
                                bind:value={customer.callDate}
                            />
                        </div>
                        <div class="mb-3 col-md-6">
                            <label for="filterChangeDate" class="form-label"
                                >Filtre Değişim Tarihi</label
                            >
                            <input
                                type="date"
                                class="form-control text-center"
                                id="filterChangeDate"
                                bind:value={customer.filterChangeDate}
                                on:change={updateCallDate}
                            />
                        </div>
                        <div class="mb-3 col-md-6">
                            <label for="membraneChangeDate" class="form-label"
                                >Membran Değişim Tarihi</label
                            >
                            <input
                                type="date"
                                class="form-control text-center"
                                id="membraneChangeDate"
                                bind:value={customer.membraneChangeDate}
                            />
                        </div>
                    </div>
                    <div class="row mt-2 mb-3">
                        <RecentOperations
                            {recentOperations}
                            {deleteOperation}
                            {addOperation}
                        />
                    </div>
                    <div class="row mt-2 mb-3">
                        <div class="col-md-6">
                            <button
                                type="button"
                                class="btn btn-info"
                                data-bs-toggle="modal"
                                data-bs-target="#customerNotesModal"
                                >Müşteri Notlarını Görüntüle</button
                            >
                        </div>
                        <div class="col-md-6">
                            <label for="reference" class="form-label"
                                >Referans</label
                            >
                            <input
                                type="text"
                                class="form-control text-center"
                                id="reference"
                                bind:value={customer.reference}
                            />
                        </div>
                    </div>
                    {#if error}
                        <div class="alert alert-danger" role="alert">
                            {error}
                        </div>
                    {/if}
                    <button type="submit" class="btn btn-outline-primary mb-5"
                        >Kaydet</button
                    >
                </fieldset>
            </form>
        </div>
        <Route {currentRoute} {params} />
    </div>
{/await}
